import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, Badge, Alert, UrgencyBadge, BloodTypeBadge, Spinner, Reveal, Skeleton, useIsMobile } from '../components/ui';
import PageLayout from '../components/layout/PageLayout';
import { Tent, Users, CalendarCheck, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

export default function OrgDashboard() {
  const [checking, setChecking] = useState(true);
  const [org, setOrg] = useState(null);
  const [camps, setCamps] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    api.get('/organizations/me')
      .then(r => setOrg(r.data))
      .catch(() => setOrg(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (checking || !org) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get('/organizations/camps/mine'),
      // city_id is an exact match; the name was a substring search that pulled
      // in every city containing it (and, globally, other countries' too).
      api.get('/requests/', {
        params: {
          status: 'open',
          ...(org.city_id ? { city_id: org.city_id } : org.city ? { city: org.city } : {}),
        },
      }),
    ]).then(([c, r]) => {
      setCamps(c.data);
      setRequests(r.data.slice(0, 5));
      setLoadError(false);
    }).catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [checking, org]);

  if (checking) {
    return (
      <PageLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px' }}><Spinner /></div>
      </PageLayout>
    );
  }

  if (!org) {
    return (
      <PageLayout>
        <Card>
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>🏢</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '8px' }}>
              Set up your organization
            </div>
            <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginBottom: '20px', lineHeight: 1.6, maxWidth: '380px', margin: '0 auto 20px' }}>
              Add your organization's details to start creating donation camps.
            </p>
            <Link to="/org/setup"><Button variant="primary">Complete setup →</Button></Link>
          </div>
        </Card>
      </PageLayout>
    );
  }

  const stats = [
    { n: camps.length, label: 'Total Camps', Icon: Tent, color: 'var(--blue)' },
    { n: camps.reduce((sum, c) => sum + (c.slots_booked || 0), 0), label: 'Registrations', Icon: Users, color: 'var(--green)' },
    { n: camps.filter(c => c.status === 'upcoming').length, label: 'Upcoming', Icon: CalendarCheck, color: 'var(--amber)' },
    { n: requests.length, label: 'Needs in City', Icon: AlertTriangle, color: 'var(--red)' },
  ];

  return (
    <PageLayout>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--gray-900)' }}>{org.name}</h1>
        <p style={{ fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '4px' }}>Track your camps and monitor blood needs.</p>
      </div>

      {!org.is_verified && (
        <Alert variant="warning">
          Your organization is pending admin verification. You can browse this dashboard, but camp creation is locked until you're approved.
        </Alert>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: '10px', marginBottom: '20px' }}>
        {stats.map(({ n, label, Icon, color }, i) => (
          <Reveal key={label} delay={i * 70}>
            <div style={{
              background: 'var(--white)', border: '1px solid var(--gray-200)',
              borderRadius: 'var(--radius)', padding: isMobile ? '14px 12px' : '18px 16px',
              boxShadow: 'var(--shadow-sm)',
              display: 'flex', alignItems: 'center', gap: '12px',
            }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={18} color={color} strokeWidth={2} />
              </div>
              <div>
                <div style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 900, color: 'var(--gray-900)', letterSpacing: '-0.04em', lineHeight: 1 }}>{n}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--gray-500)', fontWeight: 500, marginTop: '2px' }}>{label}</div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Camps */}
      <Reveal>
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Your Camps</span>
          <Link to="/org/camps/new"><Button size="sm" variant="primary">+ New Camp</Button></Link>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0, 1].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '13px 0' }}>
                <div style={{ flex: 1 }}>
                  <Skeleton width="45%" height="13px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="65%" height="11px" />
                </div>
                <Skeleton width="60px" height="20px" radius="20px" />
              </div>
            ))}
          </div>
        ) : loadError ? (
          <p style={{ fontSize: '13.5px', color: 'var(--gray-400)', padding: '12px 0' }}>Couldn't load your camps right now. Please try again.</p>
        ) : camps.length === 0 ? (
          <p style={{ fontSize: '13.5px', color: 'var(--gray-400)', padding: '12px 0' }}>
            No camps yet. <Link to="/org/camps/new">Create one →</Link>
          </p>
        ) : camps.map((c, i) => (
          // Each row opens the camp's own page — that's where the registered
          // donors and their contact details live.
          <Link
            key={c.id}
            to={`/org/camps/${c.id}`}
            style={{
              display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: '12px',
              padding: '13px 0',
              borderBottom: i < camps.length - 1 ? '1px solid var(--gray-100)' : 'none',
              flexDirection: isMobile ? 'column' : 'row',
              color: 'inherit', textDecoration: 'none',
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)' }}>{c.title}</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                {c.venue_name}, {c.city} · {new Date(c.camp_date).toLocaleDateString()}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ fontSize: '12.5px', color: 'var(--gray-500)', fontWeight: 500 }}>
                {c.slots_booked}/{c.slots_total} registered
              </span>
              <Badge color="blue">{c.status}</Badge>
              <span style={{ fontSize: '13px', color: 'var(--gray-400)' }}>→</span>
            </div>
          </Link>
        ))}
      </Card>
      </Reveal>

      {/* Nearby requests */}
      <Reveal>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--gray-900)' }}>Open Requests Nearby</span>
          <Link to="/requests" style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--red)' }}>View all →</Link>
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[0, 1].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0' }}>
                <Skeleton width="44px" height="28px" radius="8px" />
                <div style={{ flex: 1 }}>
                  <Skeleton width="45%" height="13px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="30%" height="11px" />
                </div>
              </div>
            ))}
          </div>
        ) : requests.length === 0 ? (
          <p style={{ fontSize: '13.5px', color: 'var(--gray-400)', padding: '12px 0' }}>No open requests nearby right now.</p>
        ) : requests.map((r, i) => (
          <div
            key={r.id}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              padding: '12px 0',
              borderBottom: i < requests.length - 1 ? '1px solid var(--gray-100)' : 'none',
              flexWrap: 'wrap',
            }}
          >
            <BloodTypeBadge type={r.blood_type} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-900)' }}>{r.hospital_name}</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                {r.city} · {r.units_needed} unit{r.units_needed > 1 ? 's' : ''}
              </div>
            </div>
            <UrgencyBadge urgency={r.urgency} />
          </div>
        ))}
      </Card>
      </Reveal>
    </PageLayout>
  );
}
